import { createContext, useContext } from 'react';

/**
 * Lightweight client-side cache of the most recently fetched submission
 * list. The SubmissionList component fills `orderedIds` after every list
 * fetch (in order); the SubmissionDetail component reads it to render
 * prev/next navigation that stays in sync with the active filter set.
 *
 * Lives in module-level mutable state rather than a context provider so
 * the two components do not have to share a common parent (App routes
 * them on different hash paths). React doesn't need to be notified when
 * the cache updates because Detail only reads it on user interaction
 * (prev/next click, j/k keystroke).
 */
export interface SubmissionNavCache {
  orderedIds: string[];
  /**
   * F-211 — recent {id, title} pairs the command palette can feed. Kept
   * separate from `orderedIds` so the existing prev/next signature
   * doesn't change. Capped at 20 entries.
   */
  recent: Array<{ id: string; title: string }>;
  /** Stable signature identifying the filter set the ids belong to. */
  filterKey: string;
}

const cache: SubmissionNavCache = {
  orderedIds: [],
  recent: [],
  filterKey: '',
};

const RECENT_CAP = 20;

export function setSubmissionNavCache(
  orderedIds: string[],
  filterKey: string,
  recent: Array<{ id: string; title: string }> = [],
): void {
  cache.orderedIds = orderedIds.slice();
  cache.filterKey = filterKey;
  if (recent.length > 0) {
    // Merge: keep the most recent at the head, deduped by id, cap at 20.
    const seen = new Set<string>();
    const merged: Array<{ id: string; title: string }> = [];
    for (const r of recent) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      merged.push(r);
      if (merged.length >= RECENT_CAP) break;
    }
    for (const r of cache.recent) {
      if (merged.length >= RECENT_CAP) break;
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      merged.push(r);
    }
    cache.recent = merged;
  }
}

export function getSubmissionNavCache(): SubmissionNavCache {
  return cache;
}

/**
 * Returns the previous and next submission IDs adjacent to the given
 * submission within the cached list. Undefined when the submission is
 * the first/last, or when the cache is empty.
 */
export function neighborIds(
  submissionId: string,
): { prev?: string; next?: string } {
  const ids = cache.orderedIds;
  const idx = ids.indexOf(submissionId);
  if (idx < 0) return {};
  return {
    prev: idx > 0 ? ids[idx - 1] : undefined,
    next: idx < ids.length - 1 ? ids[idx + 1] : undefined,
  };
}

/**
 * Optional React context — only used if a parent wants to override the
 * module-level cache (e.g. for tests). Components prefer the module
 * cache by default.
 */
export const SubmissionNavContext = createContext<SubmissionNavCache | null>(null);

export function useSubmissionNav(): SubmissionNavCache {
  const ctx = useContext(SubmissionNavContext);
  return ctx ?? getSubmissionNavCache();
}
